import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, AlertTriangle } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { Button } from '@/components/common'
import { useGetCowsQuery, useGetFarmsQuery, useGetHeatAlertsQuery } from '@/store'
import { CATTLE_TYPES } from '@/constants'
import { calculateAge } from '@/utils'

export default function CowListPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFarm, setSelectedFarm] = useState('')
  const [selectedCattleType, setSelectedCattleType] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(20)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedFarm, selectedCattleType])

  // Fetch cows with filters
  const { data: cowsData, isLoading, error } = useGetCowsQuery({
    search: searchTerm || undefined,
    farmId: selectedFarm || undefined,
    cattleType: (selectedCattleType || undefined) as any,
    page: currentPage,
    limit,
  })

  // Fetch farms for filter
  const { data: farmsData } = useGetFarmsQuery()

  // Fetch heat alerts
  const { data: heatAlertsData } = useGetHeatAlertsQuery()

  const cows = cowsData?.data || []
  const farms = farmsData || []

  // Create a map of cow IDs with active heat alerts
  const cowsWithHeatAlert = new Set(
    heatAlertsData?.data
      ?.filter(alert => alert.status === 'ACTIVE')
      ?.map(alert => alert.cowId) || []
  )

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">จัดการโค</h1>
            <p className="text-text-secondary mt-1">
              ทั้งหมด {cowsData?.pagination.total || 0} ตัว
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/cows/new')}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            เพิ่มโค
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-border-light">
          <div className="space-y-4">
            {/* Search and Farm Filter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  type="text"
                  placeholder="ค้นหารหัสโค, ชื่อ..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full h-input pl-10 pr-4 border border-border-medium rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-800"
                />
              </div>

              {/* Farm filter */}
              <select
                value={selectedFarm}
                onChange={(e) => setSelectedFarm(e.target.value)}
                className="h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
              >
                <option value="">ฟาร์มทั้งหมด</option>
                {farms.map((farm) => (
                  <option key={farm.id} value={farm.id}>
                    {farm.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cattle Type Filter - Clickable Buttons */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                กลุ่มวัว
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCattleType('')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCattleType === ''
                      ? 'bg-primary-800 text-white'
                      : 'bg-background-tertiary text-text-secondary hover:bg-background-gray'
                  }`}
                >
                  ทั้งหมด
                </button>
                {Object.entries(CATTLE_TYPES).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCattleType(key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCattleType === key
                        ? 'bg-primary-800 text-white'
                        : 'bg-background-tertiary text-text-secondary hover:bg-background-gray'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cow Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
          </div>
        ) : error ? (
          <div className="bg-error-light border border-error rounded-lg p-6 text-center">
            <p className="text-error">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
          </div>
        ) : cows.length === 0 ? (
          <div className="bg-background-secondary rounded-lg p-12 text-center">
            <p className="text-text-tertiary text-lg">ไม่พบข้อมูลโค</p>
            <Button
              variant="primary"
              onClick={() => navigate('/cows/new')}
              className="mt-4"
            >
              เพิ่มโคตัวแรก
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border-light overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-tertiary">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">ลำดับ</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">รหัส</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">ชื่อโค</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">กลุ่มวัว</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">เซนเซอร์</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">สถานะ</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">อายุ</th>
                  </tr>
                </thead>
                <tbody>
                  {cows.map((cow, index) => {
                    // Calculate age
                    const { totalMonths } = calculateAge(cow.birthDate)

                    return (
                      <tr
                        key={cow.id}
                        className="border-t border-border-light hover:bg-background-tertiary cursor-pointer transition-colors"
                        onClick={() => navigate(`/cows/${cow.id}`)}
                      >
                        <td className="px-4 py-3 text-sm text-text-primary">
                          {(cowsData!.pagination.page - 1) * cowsData!.pagination.limit + index + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-primary">{cow.cowCode}</td>
                        <td className="px-4 py-3 text-sm text-text-primary font-medium">
                          <div className="flex items-center gap-2">
                            <span>{cow.name || '-'}</span>
                            {cowsWithHeatAlert.has(cow.id) && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error-light text-error text-xs font-semibold"
                                title="โคเคลื่อนไหวมากกว่าปกติ"
                              >
                                <AlertTriangle className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {cow.cattleType ? (
                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                              {CATTLE_TYPES[cow.cattleType]}
                            </span>
                          ) : (
                            <span className="text-text-tertiary">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-primary">{cow.sensorId || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                              cow.healthStatus === 'HEALTHY'
                                ? 'bg-success-light text-success'
                                : cow.healthStatus === 'SICK'
                                ? 'bg-error-light text-error'
                                : cow.healthStatus === 'TREATMENT'
                                ? 'bg-warning-light text-warning'
                                : 'bg-info-light text-info'
                            }`}
                          >
                            {cow.healthStatus === 'HEALTHY'
                              ? 'สุขภาพดี'
                              : cow.healthStatus === 'SICK'
                              ? 'ป่วย'
                              : cow.healthStatus === 'TREATMENT'
                              ? 'รักษา'
                              : 'หายแล้ว'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-primary">{totalMonths} เดือน</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {cowsData && cowsData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ก่อนหน้า
            </Button>
            <span className="px-4 py-2 text-text-secondary">
              หน้า {currentPage} / {cowsData.pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === cowsData.pagination.totalPages}
            >
              ถัดไป
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
